/**
 * i18n Provider — Listto Web
 *
 * Wraps @lingui/core directly (no community fork dependency) with a
 * SolidJS-native reactive layer. Locale changes update a Signal, which
 * causes all useLingui() consumers to re-derive translations reactively
 * without any component re-render overhead.
 *
 * Lazy-loading: Only the active locale's JS catalog is fetched at startup
 * (and when the user switches language), keeping the initial bundle minimal.
 *
 * DB-sync: Reads the persisted locale from PowerSync (users table) on mount
 * and writes back via updateUserLocaleMutation when the user changes language.
 *
 * RTL support: Sets document.documentElement.dir automatically for ar / he.
 */

import { i18n, type MessageDescriptor } from "@lingui/core";
import { generateMessageId } from "@lingui/message-utils/generateMessageId";
import {
  createContext,
  createEffect,
  createSignal,
  onMount,
  type ParentComponent,
  useContext,
} from "solid-js";
import { RTL_LOCALES, SUPPORTED_LOCALES, type SupportedLocale } from "@listto/config/locales";
import { queryKeys, type Session, updateUserLocaleMutation } from "@listto/shared";
import { queryClient } from "@/lib/query-client";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useUserPreferences } from "@/providers/user-preferences-context";

// Initialize with a default locale so that early translation calls do not throw errors
i18n.load("en", {});
i18n.activate("en");

// ─── Types ────────────────────────────────────────────────────────────────────

interface I18nContextValue {
  /** Reactive current locale code */
  locale: () => SupportedLocale;
  /** Change locale — updates DB + reloads catalog */
  setLocale: (locale: SupportedLocale) => Promise<void>;
  /** True while the catalog is loading */
  isLoading: () => boolean;
  /** Reactive translation function */
  /** Reactive translation function */
  t: (message: string | MessageDescriptor, values?: Record<string, unknown>) => string;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const I18nContext = createContext<I18nContextValue>();

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}

// ─── Catalog Loader ───────────────────────────────────────────────────────────

interface CatalogModule {
  messages: Record<string, string>;
}

function assertCatalogModule(mod: unknown): asserts mod is CatalogModule {
  if (typeof mod !== "object" || mod === null || !("messages" in mod)) {
    throw new Error("Invalid catalog module");
  }
}

/**
 * Dynamically imports the compiled .js catalog for the requested locale.
 * Vite will code-split each locale into a separate chunk automatically.
 */
async function loadCatalog(locale: SupportedLocale): Promise<void> {
  try {
    const rawModule: unknown = await import(`../../locales/${locale}/messages.mjs`);
    assertCatalogModule(rawModule);
    i18n.load(locale, rawModule.messages);
    i18n.activate(locale);
  } catch (err) {
    console.warn(`[i18n] Failed to load catalog for "${locale}", falling back to "en"`, err);
    // Fall back to English if catalog missing
    const rawEnModule: unknown = await import("../../locales/en/messages.mjs");
    assertCatalogModule(rawEnModule);
    i18n.load("en", rawEnModule.messages);
    i18n.activate("en");
  }
}

/** Applies correct RTL/LTR direction on <html> element */
function applyDirection(locale: SupportedLocale) {
  document.documentElement.dir = RTL_LOCALES.has(locale) ? "rtl" : "ltr";
  document.documentElement.lang = locale;
}

/** Clean type-guard to verify if an unknown string represents a SupportedLocale */
function isSupportedLocale(locale: unknown): locale is SupportedLocale {
  return typeof locale === "string" && SUPPORTED_LOCALES.some((code) => code === locale);
}

/** Detect browser locale and match against supported locales */
function detectBrowserLocale(): SupportedLocale {
  const browser = navigator.language.split("-")[0]?.toLowerCase() ?? "en";
  return isSupportedLocale(browser) ? browser : "en";
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export const I18nProvider: ParentComponent = (props) => {
  const drizzle = useDrizzle();
  const session = useSession();
  const { preferences } = useUserPreferences();

  const [locale, setLocaleSignal] = createSignal<SupportedLocale>("en");
  const [isLoading, setIsLoading] = createSignal(true);
  let lastWrittenLocale: SupportedLocale | null = null;

  /**
   * Changes the active locale:
   * 1. Loads the catalog chunk
   * 2. Updates the reactive signal (triggers re-renders)
   * 3. Applies HTML dir attribute
   * 4. Persists to DB if authenticated
   */
  const setLocale = async (newLocale: SupportedLocale): Promise<void> => {
    if (newLocale === locale()) {
      return;
    }
    lastWrittenLocale = newLocale; // Mark this as a manually written locale to prevent loops
    setIsLoading(true);
    await loadCatalog(newLocale);
    setLocaleSignal(newLocale);
    applyDirection(newLocale);
    setIsLoading(false);

    // Persist preference to localStorage
    try {
      localStorage.setItem("@app_locale", newLocale);
    } catch (err) {
      console.warn("[i18n] Could not persist locale to localStorage:", err);
    }

    // Update local TanStack Query cache to prevent loops/stale overrides
    try {
      queryClient.setQueryData<Session>(queryKeys.auth.session(), (old) => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          user: {
            ...old.user,
            locale: newLocale,
          },
        };
      });
    } catch (err) {
      console.warn("[i18n] Could not update session cache:", err);
    }

    // Persist preference to DB
    const dbInstance = drizzle();
    const userId = session.userId();
    if (dbInstance && userId) {
      try {
        await updateUserLocaleMutation(dbInstance, userId, newLocale);
      } catch (err) {
        console.warn("[i18n] Could not persist locale to DB:", err);
      }
    }
  };

  // On mount: load the initial locale (from localStorage or browser)
  onMount(() => {
    const init = async () => {
      try {
        const storedLocale = localStorage.getItem("@app_locale");
        const userLocale = isSupportedLocale(storedLocale) ? storedLocale : detectBrowserLocale();

        await loadCatalog(userLocale);
        setLocaleSignal(userLocale);
        applyDirection(userLocale);
      } catch (err) {
        console.warn("[i18n] Failed to initialize i18n:", err);
      } finally {
        setIsLoading(false);
      }
    };
    void init();
  });

  // React to PowerSync database changes (Single Source of Truth)
  createEffect(() => {
    const remoteLocale = preferences()?.locale;

    if (isSupportedLocale(remoteLocale)) {
      // If the database has synchronized with our local change, release the block
      if (remoteLocale === lastWrittenLocale) {
        console.log(`[i18n] 🔄 DB synced with local change: "${remoteLocale}"`);
        lastWrittenLocale = null;
        return;
      }

      // If we have a pending local change, ignore stale database updates
      if (lastWrittenLocale !== null) {
        console.log(
          `[i18n] ⏳ Ignoring "${remoteLocale}" from DB due to pending local change to "${lastWrittenLocale}"`,
        );
        return;
      }

      // Otherwise, apply the DB preference if it differs from the current UI
      if (remoteLocale !== locale()) {
        console.log(
          `[i18n] 🌐 DB preference changed to "${remoteLocale}" (UI is "${locale()}") -> applying`,
        );
        const loadSync = async () => {
          await loadCatalog(remoteLocale);
          setLocaleSignal(remoteLocale);
          applyDirection(remoteLocale);
          try {
            localStorage.setItem("@app_locale", remoteLocale);
          } catch (err) {
            console.warn("[i18n] Could not sync locale to localStorage:", err);
          }
        };
        void loadSync();
      }
    }
  });

  const translate = (
    message: string | MessageDescriptor,
    values?: Record<string, unknown>,
  ): string => {
    locale(); // Establish dynamic reactive dependency on SolidJS signal
    if (typeof message === "string") {
      // El catálogo compilado usa ids HASH (generateMessageId), igual que el macro
      // `msg`...``. Un string plano (`t("My Day")`) buscaría id="My Day" y no
      // resolvería (mostraría inglés en cualquier idioma). Computamos el mismo id
      // que el macro para que `t("literal")` traduzca igual que `t(msg`literal`)`,
      // con el propio texto como fallback. (Fix del bug "idioma no se refleja".)
      return i18n._({ id: generateMessageId(message), message, values });
    }
    // Los values también aplican a descriptores (van DENTRO del descriptor en
    // la API de Lingui): sin ellos, mensajes como msg`...{{date}}...`
    // renderizaban el placeholder vacío ("{}"). El descriptor se construye en
    // una variable aparte: el extractor de Lingui no sabe parsear un spread
    // inline dentro de `i18n._()` y aborta la extracción del archivo.
    const descriptor: MessageDescriptor = {
      ...message,
      values: { ...message.values, ...values },
    };
    return i18n._(descriptor);
  };

  return (
    <I18nContext.Provider value={{ locale, setLocale, isLoading, t: translate }}>
      {/*
        Reactivity is provided by the locale() signal read inside translate();
        the app uses useI18n().t exclusively (no @lingui-solid/solid <Trans>/
        useLingui macros), so no extra runtime provider wrapper is needed.
      */}
      {props.children}
    </I18nContext.Provider>
  );
};
